import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense, useMemo } from 'react';
import AboutModal from '@components/layout/AboutModal';
import CanvasErrorBoundary from '@components/layout/CanvasErrorBoundary';
import MobileGate from '@components/layout/MobileGate';
import ResizeHandle from '@components/layout/ResizeHandle';
import Sidebar from '@components/layout/Sidebar';
import SplashScreen from '@components/layout/SplashScreen';
import TopBar from '@components/layout/TopBar';
import ViewportSkeleton from '@components/layout/ViewportSkeleton';
import WebGLGate from '@components/layout/WebGLGate';
import ToastContainer from '@components/ui/Toast';
import { useIsMobile } from '@hooks/useMediaQuery';
import { useKeyboardShortcuts } from '@hooks/useKeyboardShortcuts';
import { useStepRouting } from '@hooks/useStepRouting';
import { useUIStore } from '@stores/useUIStore';
import { hasWebGL } from '@utils/webglSupport';

const Viewport3D = lazy(() => import('@features/viewport/Viewport3D'));

/**
 * App shell — DESIGN.md §5 layout with resizable sidebar.
 *
 *   [ TopBar                                     56px ]
 *   [ Sidebar (40% default) | handle | Viewport (60%) ]
 *
 * The sidebar width is stored as a percentage (25–55%) and driven by
 * the drag handle. Content inside the sidebar reflows responsively.
 */
export default function App() {
  const isMobile = useIsMobile();
  const isSplashVisible = useUIStore((s) => s.isSplashVisible);
  const webglAvailable = useMemo(() => hasWebGL(), []);
  // sidebarWidthPct is consumed directly by Sidebar
  useKeyboardShortcuts();
  useStepRouting();

  if (isMobile) {
    return <MobileGate />;
  }

  if (!webglAvailable) {
    return <WebGLGate />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        overflow: 'hidden',
      }}
    >
      <TopBar />
      <div
        style={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
        }}
      >
        <Sidebar />

        {/* Drag handle */}
        <ResizeHandle />

        {/* Viewport — takes the remaining space */}
        <main
          aria-label="3D viewport"
          style={{ flex: 1, position: 'relative', minWidth: 0 }}
        >
          <CanvasErrorBoundary>
            <Suspense fallback={<ViewportSkeleton />}>
              <Viewport3D />
            </Suspense>
          </CanvasErrorBoundary>
        </main>
      </div>

      <AnimatePresence>
        {isSplashVisible && <SplashScreen key="splash" />}
      </AnimatePresence>

      <AboutModal />
      <ToastContainer />
    </div>
  );
}
